import { useRef } from 'react';
import {
  IonPage,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonText,
  IonButton,
  IonLoading,
  IonImg
} from '@ionic/react';
import { observer } from 'mobx-react-lite';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import classes from './auth.module.scss';
import { useThemeLogo, useTheme } from 'libs/theme/useTheme';

import userStore from 'stores/user.store';

const AuthLogin = () => {
  const isLoading = userStore.getIsLoading();
  const usernameRef = useRef();
  const passwordRef = useRef();
  const logoUrl = useThemeLogo();
  const theme = useTheme();

  const loginForm = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Email is not valid!').required('Required!'),
      password: Yup.string().required('Required!')
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      userStore.setIsAuthenticating(true);
      try {
        await userStore.authenticate(values.email, values.password);
        userStore.setIsAuthenticating(false);
      } catch (error) {
        userStore.setIsAuthenticating(false);
        loginForm.resetForm();
      }
    }
  });

  const checkAutoFill = async () => {
    const usernameDom = usernameRef.current?.querySelectorAll('input')[0].value;
    const passwordDom = passwordRef.current?.querySelectorAll('input')[0].value;
    if (usernameDom !== loginForm.values.email) {
      await loginForm.setFieldValue('email', usernameDom);
      await loginForm.handleChange('email');
      await loginForm.handleBlur('email');
    }

    if (passwordDom !== loginForm.values.password) {
      await loginForm.setFieldValue('password', passwordDom);
      await loginForm.handleChange('password');
      await loginForm.handleBlur('password');
    }
  };

  return (
    <IonPage>
      <IonContent>
        <div className={classes.backImage}>
          <div className={classes.overlay}></div>
        </div>
        <div className={classes.center}>
          <div className={classes.loginLogo}>
            <IonImg 
              src={logoUrl || require('assets/logo.png').default} 
              alt='Barwis Logo' 
            />
          </div>
          <IonText color='primary'>
            <h3>Sign In</h3>
          </IonText>
          <form onSubmit={loginForm.handleSubmit} className='form'>
            <IonList>
              <IonItem>
                <IonLabel position='floating'>Email</IonLabel>
                <IonInput
                  type='email'
                  name='email'
                  inputMode='email'
                  value={loginForm.values.email}
                  onLoad={(e) => console.log('loaded')}
                  onIonChange={async (e) => {
                    loginForm.handleChange('email', e);
                    await checkAutoFill();
                  }}
                  onIonBlur={loginForm.handleBlur}
                  autocomplete='username'
                  ref={usernameRef}
                ></IonInput>
              </IonItem>
              {loginForm.touched.email && loginForm.errors.email && (
                <IonText color='danger'>
                  <p className='ion-padding-start'>{loginForm.errors.email}</p>
                </IonText>
              )}
              <IonItem className='ion-margin-top'>
                <IonLabel position='floating'>Password</IonLabel>
                <IonInput
                  type='password'
                  name='password'
                  value={loginForm.values.password}
                  onIonChange={async (e) => {
                    loginForm.handleChange('password', e);
                    await checkAutoFill();
                  }}
                  onIonBlur={loginForm.handleBlur}
                  autocomplete='current-password'
                  ref={passwordRef}
                ></IonInput>
              </IonItem>
              {loginForm.touched.password && loginForm.errors.password && (
                <IonText color='danger'>
                  <p className='ion-padding-start'>
                    {loginForm.errors.password}
                  </p>
                </IonText>
              )}
              <IonItem className={`ion-margin-top ${classes.actionButton}`}>
                <IonButton
                  type='submit'
                  color='primary'
                  fill='outline'
                  size='default'
                >
                  Login
                </IonButton>
              </IonItem>
              <IonItem className={`ion-margin-top ${classes.actionButton}`}>
                <IonButton
                  type='button'
                  color='primary'
                  fill='clear'
                  size='default'
                  disabled={
                    !loginForm.values.email || Boolean(loginForm.errors.email)
                  }
                  onClick={() =>
                    userStore.sendPasswordRestLink(loginForm.values.email)
                  }
                >
                  Forget password?
                </IonButton>
              </IonItem>
            </IonList>
          </form>
        </div>
        <IonLoading isOpen={isLoading} message='Please wait...' />
      </IonContent>
    </IonPage>
  );
};

export default observer(AuthLogin);
